import {
  DataObjectOutputType,
  errorMap,
  Form,
  Main,
  Padding,
  Stack,
  TotalEntries,
  ValidPeriodOptions,
  DataObjectVersionFilters,
  DataObjectDescriptionFilter,
  useQueryParams,
  useReportVersionDataQuery,
  Table,
  EMPTY_PAGE,
  CustomPagination,
  useReportVersionDataInfoQuery,
  Spinner,
  type ReportVersionDataInfoType,
  useDirectory,
  removeNullish,
  useDataObjectGroupsWithDescriptionsQuery,
  DataObjectGroupType,
  useGroupDescriptionOptions,
  ISODate,
  useDataObjectVersionsQuery,
  DashboardType,
  zIsoDate,
  EmptyData,
  getForbiddenErrorCode,
  InfoIconUrl,
} from '@atlas-frontend/sdk';
import { keepPreviousData } from '@tanstack/react-query';
import { type AccessorKeyColumnDef, CellContext } from '@tanstack/react-table';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import type { ReturnType } from 'tsafe';
import { path } from '../../../paths';
import { UiLink } from '../../../paths/UiLink';
import translations from './Report.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import z from 'zod/v3';
import { Divider, Flex, Radio } from 'antd';
import {
  type FC,
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { VisualValidationHeader } from '../VisualValidationHeader';
import { VisualValidationActionButtons } from '../VisualValidationActionButtons';
import { CommentsModal, hasDescriptionId } from '../modals/CommentsModal';
import {
  AlgorithmsModal,
  ModalData,
  SourcesModal,
} from '../modals/DetailsModal';
import { QueryParams } from '../../../paths/withParams';

const { addTranslations, t } = i18nNsFactory('visual-validation-report');
addTranslations(translations);

const ReportFormSchema = z.object({
  groupDescriptionRef: z
    .string(errorMap(() => t('validation.groupDescriptionRef.required')))
    .min(1),
  valueDate: zIsoDate(() => t('validation.valueDate.required')),
  version: z.number(errorMap(() => t('validation.version.required'))).min(1),
});
type ReportFormType = z.infer<typeof ReportFormSchema>;

export const Report: FC = () => (
  <Form schema={ReportFormSchema} blockNavigationOnFirstChange={false}>
    <Main>
      <ReportInner />
    </Main>
  </Form>
);

const ReportInner: FC = () => {
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [algorithmsModalData, setAlgorithmsModalData] =
    useState<ModalData | null>(null);
  const [sourcesModalData, setSourcesModalData] = useState<ModalData | null>(
    null,
  );

  const [periodOptions, setPeriodOptions] = useState<ValidPeriodOptions>({});
  const [queryParams, setQueryParams] = useQueryParams();

  const {
    valueDate,
    descriptionOptions,
    selectedDescription,
    isFetchingDescriptions,
    versions,
    selectedVersion,
    isFetchingVersions,
    refetchDescriptions,
  } = useReportSelection(queryParams, setQueryParams);
  const descriptionId = selectedDescription?.descriptionId;

  const openDetailsModal = useCallback(
    (modalType: 'algorithms' | 'sources') => {
      if (!selectedVersion || !selectedDescription) return;
      const data = {
        type: DashboardType.REPORT,
        version: selectedVersion,
        descriptionOption: selectedDescription,
      } as const;
      if (modalType === 'algorithms') setAlgorithmsModalData(data);
      else setSourcesModalData(data);
    },
    [selectedVersion, selectedDescription],
  );

  const [visibleColumnType, setVisibleColumnType] =
    useState<VisibleColumnType>('keys');

  const [directoryInfo, setDirectoryInfo] = useState<DirectoryInfo | undefined>(
    undefined,
  );
  return (
    <Main.Col>
      <Main.Header />
      <Main.Content padding={false} id="visual-validation-report">
        {isCommentsModalOpen && selectedDescription && (
          <CommentsModal
            descriptionOption={selectedDescription}
            type="single"
            valueDate={valueDate}
            refetchDescriptions={refetchDescriptions}
            onClose={() => setIsCommentsModalOpen(false)}
          />
        )}
        {algorithmsModalData && (
          <AlgorithmsModal
            {...algorithmsModalData}
            onClose={() => setAlgorithmsModalData(null)}
          />
        )}
        {sourcesModalData && (
          <SourcesModal
            {...sourcesModalData}
            onClose={() => setSourcesModalData(null)}
          />
        )}
        <ContentWrapper>
          <VisualValidationHeader
            infoIconUrl={InfoIconUrl.VISUAL_VALIDATION_REPORT}
          />
          <Flex vertical gap="small">
            <HorizontalSticky>
              <Flex vertical gap="small">
                <Padding horizontal>
                  <DataObjectDescriptionFilter
                    options={descriptionOptions}
                    isLoading={isFetchingDescriptions}
                    outputType={DataObjectOutputType.REPORT}
                    onChange={() => setPeriodOptions({})}
                  />
                </Padding>
                <Divider />
              </Flex>
            </HorizontalSticky>
            <ReportDirectory
              visibleColumnType={visibleColumnType}
              onDirectoryInfo={setDirectoryInfo}
              actions={
                <TableActionsWrapper
                  justify="space-between"
                  align="end"
                  gap="small"
                  wrap="wrap"
                >
                  <Flex gap="small" wrap="wrap">
                    <DataObjectVersionFilters
                      versions={versions || []}
                      isLoading={isFetchingVersions}
                      outputType={DataObjectOutputType.REPORT}
                      periodOptions={periodOptions}
                      setPeriodOptions={setPeriodOptions}
                    />
                  </Flex>
                  <Flex gap="small" wrap="wrap" align="center">
                    <Radio.Group
                      value={visibleColumnType}
                      onChange={(event) => {
                        setVisibleColumnType(event.target.value);
                      }}
                      options={[
                        {
                          value: 'keys',
                          label: t('visibleColumnType.keys'),
                        },
                        {
                          value: 'dimensions',
                          label: t('visibleColumnType.dimensions'),
                        },
                        {
                          value: 'all',
                          label: t('visibleColumnType.all'),
                        },
                      ]}
                    />
                    <VisualValidationActionButtons
                      hasComments={selectedDescription?.hasComments || false}
                      hasVersions={!!selectedVersion}
                      hasDescription={!!descriptionId}
                      onCommentsClick={() => {
                        setIsCommentsModalOpen(true);
                      }}
                      onAlgorithmsClick={() => openDetailsModal('algorithms')}
                      onSourcesClick={() => openDetailsModal('sources')}
                    />
                  </Flex>
                </TableActionsWrapper>
              }
            />
          </Flex>
        </ContentWrapper>
      </Main.Content>
      {directoryInfo && (
        <Main.Footer>
          <TotalEntries count={directoryInfo.totalElements} />
          <FooterPagination {...directoryInfo.paginationProps} />
        </Main.Footer>
      )}
    </Main.Col>
  );
};

type VisibleColumnType = 'keys' | 'dimensions' | 'all';

type DirectoryInfo = {
  totalElements: number;
  paginationProps: ReturnType<typeof useDirectory>['paginationProps'];
};
type OnDirectoryInfo = (info: DirectoryInfo | undefined) => void;

interface ReportDirectoryProps {
  visibleColumnType: VisibleColumnType;
  onDirectoryInfo: OnDirectoryInfo;
  actions?: ReactNode;
}

const ReportDirectory: FC<ReportDirectoryProps> = (props) => {
  const { visibleColumnType, onDirectoryInfo, actions } = props;
  const dataObjectVersionId = useWatch({ name: 'version' });

  const {
    data: versionDataInfo,
    isFetching,
    error,
  } = useReportVersionDataInfoQuery(
    {
      dataObjectVersionId: dataObjectVersionId
        ? Number(dataObjectVersionId)
        : 0,
    },
    {
      enabled: Boolean(dataObjectVersionId),
    },
  );
  const forbiddenErrorCode = getForbiddenErrorCode(error);

  useEffect(() => {
    if (isFetching || !dataObjectVersionId || !versionDataInfo) {
      onDirectoryInfo(undefined);
    }
  }, [onDirectoryInfo, isFetching, dataObjectVersionId, versionDataInfo]);

  if (isFetching || !dataObjectVersionId || !versionDataInfo) {
    return (
      <>
        {isFetching && <Spinner overlay />}
        <Padding horizontal bottom>
          {actions}
        </Padding>
        <EmptyData>
          {forbiddenErrorCode
            ? t(`error.code.${forbiddenErrorCode}`)
            : t('error.code.noData')}
        </EmptyData>
      </>
    );
  }

  return (
    <ReportVersionDataDirectory
      dataObjectVersionId={dataObjectVersionId}
      versionDataInfo={versionDataInfo}
      visibleColumnType={visibleColumnType}
      onDirectoryInfo={onDirectoryInfo}
      actions={actions}
    />
  );
};

interface ReportVersionDataDirectoryProps {
  dataObjectVersionId: number;
  versionDataInfo: ReportVersionDataInfoType;
  visibleColumnType: VisibleColumnType;
  onDirectoryInfo: OnDirectoryInfo;
  actions?: ReactNode;
}

const ReportVersionDataDirectory: FC<ReportVersionDataDirectoryProps> = (
  props,
) => {
  const {
    dataObjectVersionId,
    versionDataInfo,
    visibleColumnType,
    onDirectoryInfo,
    actions,
  } = props;

  const [columnKeys, columns] = useMemo(() => {
    const columnKeys: string[] = [];
    const columns: AccessorKeyColumnDef<any, any>[] = [];

    for (const columnInfo of versionDataInfo.columns) {
      const isKey = columnInfo.code === 'xKEY';
      const isValueDef = columnInfo.code === 'xVALUE_DEF';
      const isDimension = 'isDimension' in columnInfo;
      const isIndicator = 'isIndicator' in columnInfo;

      if (
        isValueDef ||
        (visibleColumnType === 'keys' && isDimension) ||
        (visibleColumnType === 'dimensions' && isKey) ||
        isIndicatorConfStatus(columnInfo.code)
      ) {
        continue;
      }

      columnKeys.push(columnInfo.code);
      columns.push({
        accessorKey: columnInfo.code,
        header: columnInfo.name,
        meta: isKey
          ? {
              width: 400,
              className: 'sticky-left',
            }
          : isIndicator
            ? {
                width: 150,
                textAlign: 'right',
              }
            : { width: undefined },
        ...(isIndicator
          ? {
              cell: (props: any) => (
                <IndicatorCell
                  {...props}
                  indicator={columnInfo}
                  dataObjectVersionId={dataObjectVersionId}
                />
              ),
            }
          : {}),
      });
    }

    return [columnKeys, columns];
  }, [dataObjectVersionId, versionDataInfo.columns, visibleColumnType]);

  const {
    tableProps,
    paginationProps,
    setTotal,
    queryParameters: { page, size, sort, ...allFilterParams },
  } = useDirectory({
    queryFilterKeys: columnKeys,
  });

  const filterParams = useMemo(
    () => removeNullish(allFilterParams),
    [allFilterParams],
  );

  const { data, isFetching } = useReportVersionDataQuery(
    {
      page,
      size,
      sort,
      filterParams,
      dataObjectVersionId: dataObjectVersionId
        ? Number(dataObjectVersionId)
        : 0,
    },
    {
      enabled: Boolean(dataObjectVersionId),
      placeholderData: keepPreviousData,
    },
  );

  useEffect(() => {
    setTotal(data?.totalElements);
  }, [setTotal, data?.totalElements]);

  useEffect(() => {
    if (data) {
      onDirectoryInfo({ totalElements: data.totalElements, paginationProps });
    }
  }, [onDirectoryInfo, data, paginationProps]);

  return (
    <Padding horizontal bottom>
      <Flex vertical gap="small">
        <Table
          name="visual-validation-report-vaersion-data-table"
          scrollContainerSelector="#visual-validation-report"
          sticky
          virtual
          manualFiltering
          manualSorting
          columns={columns}
          data={(data || EMPTY_PAGE).content}
          isLoading={isFetching}
          actions={actions}
          actionsInFront
          actionsWrapperProps={{
            align: 'end',
            style: {
              position: 'sticky',
              left: '50%',
              transform: 'translateX(-50%)',
              marginInline: '-999999px',
              width: '100%',
            },
          }}
          {...tableProps}
        />
      </Flex>
    </Padding>
  );
};

const useReportSelection = (
  queryParameters: Partial<QueryParams>,
  setQueryParams: (params: Partial<QueryParams>) => void,
) => {
  const { groupDescriptionRef, valueDate, version } =
    useWatch<ReportFormType>();
  const { reset } = useFormContext<ReportFormType>();

  const queryGroupDescriptionRef =
    typeof queryParameters.groupDescriptionRef === 'string'
      ? queryParameters.groupDescriptionRef
      : undefined;
  const queryValueDate = queryParameters.valueDate as ISODate | undefined;
  const queryVersion = queryParameters.version
    ? Number(queryParameters.version)
    : undefined;

  // Update query parameters when form values change
  useEffect(() => {
    setQueryParams({
      groupDescriptionRef,
      valueDate,
      version: version != undefined ? String(version) : undefined,
    });
  }, [groupDescriptionRef, valueDate, version, setQueryParams]);

  // Reset form values when relevant query parameters change
  useEffect(() => {
    reset({
      ...queryParameters,
      groupDescriptionRef: queryGroupDescriptionRef,
      valueDate: queryValueDate,
      version: queryVersion,
    });
  }, [
    reset,
    queryParameters,
    queryGroupDescriptionRef,
    queryValueDate,
    queryVersion,
  ]);

  const {
    data: dataObjectGroupsWithDescriptions,
    refetch: refetchDescriptions,
    isFetching: isFetchingDescriptions,
  } = useDataObjectGroupsWithDescriptionsQuery(
    {
      groupType: DataObjectGroupType.O,
      outputType: DataObjectOutputType.REPORT,
    },
    {
      placeholderData: keepPreviousData,
    },
  );
  const descriptionOptions = useGroupDescriptionOptions(
    dataObjectGroupsWithDescriptions,
    false,
  );

  const selectedDescription = useMemo(
    () =>
      descriptionOptions
        .flatMap((options) => options)
        .filter(hasDescriptionId)
        .find((option) => option.value === groupDescriptionRef),
    [descriptionOptions, groupDescriptionRef],
  );
  const descriptionId = selectedDescription?.descriptionId;

  const { data: versions, isFetching: isFetchingVersions } =
    useDataObjectVersionsQuery(
      {
        id: descriptionId!,
        valueDate: valueDate,
      },
      {
        enabled: !!descriptionId && !!valueDate,
        placeholderData: keepPreviousData,
      },
    );
  const selectedVersion = useMemo(
    () => versions?.find((ver) => ver.id === version),
    [versions, version],
  );

  return {
    valueDate,
    descriptionOptions,
    selectedDescription,
    isFetchingDescriptions,
    refetchDescriptions,
    versions,
    selectedVersion,
    isFetchingVersions,
  };
};

const HorizontalSticky = styled.div`
  position: sticky;
  left: 50%;
  transform: translateX(-50%);
  margin-inline: -999999px;
  width: 100%;
`;

const TableActionsWrapper = styled(Flex)`
  flex: 1;
`;

const ContentWrapper = styled(Stack)`
  width: 100%;
  max-width: 120em;
  height: 100%;
  margin-right: auto;
  margin-left: auto;
`;

const FooterPagination = styled(CustomPagination)`
  margin-left: auto;
`;

const IndicatorCell: FC<
  CellContext<any, any> & {
    indicator: {
      code: string;
      name: string;
    };
    dataObjectVersionId: number;
  }
> = memo((props) => (
  <UiLink
    id={`indicator-${props.indicator.code}`}
    path={path.visualValidationReportIndicatorValue}
    params={{
      dataObjectVersionId: props.dataObjectVersionId,
      indicatorCode: props.indicator.code,
      key: props.row.original.xKEY,
    }}
    target="_blank"
  >
    {props.renderValue()}
  </UiLink>
));

const isIndicatorConfStatus = (
  code: unknown,
): code is `x${string}_INDICATOR_CONF_STATUS_DATA` =>
  typeof code === 'string' && /^x\w+_INDICATOR_CONF_STATUS_DATA$/.test(code);
